import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import '../Students/StudentsPage.css';

const LessonsPage = () => {
    const [lessons, setLessons] = useState([]);
    const [levels, setLevels] = useState([]);
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingLesson, setEditingLesson] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState('');

    const [formData, setFormData] = useState({
        levelId: '',
        programId: null,
        title: '',
        description: '',
        sortOrder: 0
    });

    useEffect(() => {
        loadData();
    }, [selectedLevel]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [areasResponse, levelsResponse, lessonsResponse] = await Promise.all([
                adminAPI.getAreas(),
                adminAPI.getLevels(),
                adminAPI.getLessons(selectedLevel || undefined)
            ]);
            setAreas(areasResponse.data.areas || []);
            setLevels(levelsResponse.data.levels || []);
            setLessons(lessonsResponse.data.lessons || []);
            setError('');
        } catch (err) {
            setError('Ошибка при загрузке данных');
            console.error(err);
            // Устанавливаем пустые массивы при ошибке
            setAreas([]);
            setLevels([]);
            setLessons([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingLesson) {
                await adminAPI.updateLesson(editingLesson.id, formData);
            } else {
                await adminAPI.createLesson(formData);
            }
            
            setShowModal(false);
            setEditingLesson(null);
            resetForm();
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Ошибка при сохранении урока');
        }
    };

    const handleEdit = (lesson) => {
        setEditingLesson(lesson);
        setFormData({
            levelId: lesson.level_id,
            programId: lesson.program_id || null,
            title: lesson.title,
            description: lesson.description || '',
            sortOrder: lesson.sort_order || 0
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот урок? Все связанные тексты и упражнения также будут удалены!')) {
            return;
        }

        try {
            await adminAPI.deleteLesson(id);
            loadData();
        } catch (err) {
            setError('Ошибка при удалении урока');
        }
    };

    const resetForm = () => {
        setFormData({
            levelId: '',
            programId: null,
            title: '',
            description: '',
            sortOrder: 0
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingLesson(null);
        resetForm();
    };

    if (loading) {
        return <div className="loading">Загрузка...</div>;
    }

    return (
        <div className="container">
            <div className="page-header">
                <div>
                    <h1>Уроки</h1>
                    <p>Управление уроками и их содержимым</p>
                </div>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowModal(true)}
                >
                    <FiPlus /> Добавить урок
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="card" style={{ marginBottom: '20px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Фильтр по уровню:</label>
                    <select 
                        value={selectedLevel} 
                        onChange={(e) => setSelectedLevel(e.target.value)}
                        style={{ width: '300px' }}
                    >
                        <option value="">Все уровни</option>
                        {levels.map(level => (
                            <option key={level.id} value={level.id}>
                                {level.area_name} - {level.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="card">
                <div className="students-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Порядок</th>
                                <th>Уровень</th>
                                <th>Название урока</th>
                                <th>Описание</th>
                                <th>Статус</th>
                                <th>Дата создания</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!lessons || lessons.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center' }}>
                                        Нет уроков. Создайте первый урок.
                                    </td>
                                </tr>
                            ) : (
                                lessons.map((lesson) => (
                                    <tr key={lesson.id}>
                                        <td>{lesson.sort_order}</td>
                                        <td>{lesson.level_name}</td>
                                        <td><strong>{lesson.title}</strong></td>
                                        <td>{lesson.description || '—'}</td>
                                        <td>
                                            <span className={`status-badge ${lesson.is_active ? 'active' : 'inactive'}`}>
                                                {lesson.is_active ? 'Активен' : 'Неактивен'}
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(lesson.created_at).toLocaleDateString('ru-RU')}
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => handleEdit(lesson)}
                                                    title="Редактировать"
                                                >
                                                    <FiEdit />
                                                </button>
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={() => handleDelete(lesson.id)}
                                                    title="Удалить"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingLesson ? 'Редактировать урок' : 'Добавить урок'}</h2>
                            <button onClick={handleCloseModal} className="modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-group">
                                <label>Уровень *</label>
                                <select
                                    name="levelId"
                                    value={formData.levelId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Выберите уровень</option>
                                    {levels.map(level => (
                                        <option key={level.id} value={level.id}>
                                            {level.area_name} - {level.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Название урока *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="Например: Урок 1. Приветствие"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Описание</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Краткое описание урока"
                                    rows="4"
                                />
                            </div>

                            <div className="form-group">
                                <label>Порядок сортировки</label>
                                <input
                                    type="number"
                                    name="sortOrder"
                                    value={formData.sortOrder}
                                    onChange={handleInputChange}
                                    min="0"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn" onClick={handleCloseModal}>
                                    Отмена
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingLesson ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .status-badge.active {
                    background-color: #f6ffed;
                    color: #52c41a;
                    border: 1px solid #b7eb8f;
                }
                .status-badge.inactive {
                    background-color: #fff2f0;
                    color: #f5222d;
                    border: 1px solid #ffccc7;
                }
            `}</style>
        </div>
    );
};

export default LessonsPage;
